import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import styles from "./ReturnAndBorrowWithSave.module.scss";
import { BorrowedProduct, getBorrows } from "src/utils/ajaxUtils";
import { getCoverImageUrlFromAsinAndHeight, getCoverImageUrlFromPhysicalIdAndHeight } from "src/utils/asinUtils";
import AcquisitionManagerContext, { onFailure, onSuccess } from "src/contexts/AcquisitionManagerContext";
import debug from "src/utils/debugUtils";
import DeviceContext from "src/contexts/DeviceContext";
import { getProgramLogo } from "src/utils/programLogoUtils";
import TranslationsContext from "src/contexts/TranslationsContext";
import Spinner from "../Spinner/Spinner";
import focusHelper from "src/utils/focusHelper";
import HeartCxContext from "src/contexts/HeartCxContext";
import { addToWishlist, removeFromWishlistsGQL } from "src/utils/ajaxUtils";
import "@amzn/books-design-system-web/build/dist/src/components/BDS/Icon/Icon";
import WorkflowContext from "src/contexts/WorkflowContext";
import { getQvMetadata } from "src/utils/asinMetadataUtils";
import { Theme } from "../../utils/themeUtils";

const FilledHeartIcon = () => (
    <bds-icon iconName="heart-filled.svg" iconSize="24px" />
);

const UnfilledHeartIcon = () => (
    <bds-icon iconName="heart.svg" iconSize="24px" />
);

interface ReturnAndBorrowWithSaveProps {
    data: BorrowLimitData;
    callback: VoidFunction;
    dismiss: () => void;
}

interface BookImageProps {
    item: BorrowedProduct;
    isSelected: boolean;
    selectionToggle: () => void;
}

const BookImage: React.FC<BookImageProps> = ({ item, isSelected, selectionToggle }: BookImageProps) => {
    const deviceContext = useContext(DeviceContext);
    const translations = useContext(TranslationsContext);
    const targetHeight = 300;
    const imageData = item?.product?.productImages?.images?.[0]?.lowRes;
    const altText = item?.product?.productImages?.altText;
    const imageUrl = imageData?.physicalId
        ? getCoverImageUrlFromPhysicalIdAndHeight( imageData.physicalId, targetHeight, imageData.extension )
        : getCoverImageUrlFromAsinAndHeight(item?.product?.asin ?? "", targetHeight);

    // Get program logo
    const programLogo = item?.program ? getProgramLogo(item.program, Theme.DARK, deviceContext.domain, deviceContext.enableKuRebrand) : undefined;
    return (
        <div className={styles.bookCover} role="checkbox" aria-checked={isSelected} onClick={selectionToggle} tabIndex={0} aria-label={altText}>
            <img
                className={isSelected ? styles.selectedBook : styles.unselectedBook}
                src={imageUrl}
                alt={""}
                aria-hidden={true}
            />
            {isSelected && (
                <div className={styles.checkMarkContainer}>
                    <div className={styles.checkMarkIcon} />
                </div>
            )}
            {programLogo && (
                <div className={styles.programBadgeContainer}>
                    <img 
                        className={`${styles.programBadge} ${styles[programLogo.extraStyleName || ""]}`}
                        src={programLogo.src} 
                        alt={translations.getText(programLogo.altKey)} 
                    />
                </div>
            )}
        </div>
    );
};

const ReturnAndBorrowWithSave: React.FC<ReturnAndBorrowWithSaveProps> = ({ data, dismiss, callback }: ReturnAndBorrowWithSaveProps) => {
    const deviceContext = useContext(DeviceContext);
    const translations = useContext(TranslationsContext);
    const acquisitionManagerContext = useContext(AcquisitionManagerContext);
    const heartCxContext = useContext(HeartCxContext);
    const a11yInitialTarget = useRef<HTMLDivElement>(null);
    const workflowContext = useContext(WorkflowContext);

    const [borrowsFetched, setBorrowsFetched] = useState(false);
    const [fetchInProgress, setFetchInProgress] = useState(false);
    const [borrows, setBorrows] = useState<BorrowedProduct[]>([]);
    const [selectedItems, setSelectedItems] = useState<number[]>([]);
    const [saveInProgress, setSaveInProgress] = useState(false);
    const [returnInProgress, setReturnInProgress] = useState(false);

    // Metadata state management
    const [metadata, setMetadata] = useState<QuickViewAsinMetadata>({ asin: data.asin });
    const [hasFetchedMetadata, setHasFetchedMetadata] = useState(false);

    // Get heart state from HeartCx context using the loaded metadata
    const isBookInWishlist = heartCxContext.isHearted(data.asin, metadata?.isHearted ?? false);
    
    const fetchBorrows = useCallback(() => {
        if (borrowsFetched || fetchInProgress) {
            return;
        }
        setFetchInProgress(true);
        const program = data.action.actionProgram;
        getBorrows(data.asin, program.programCode, program.channelCode)
            .then((response) => {
                debug.log(response);
                setBorrows(response.borrowedProducts || []);
                setBorrowsFetched(true);
            })
            .finally(() => setFetchInProgress(false));
    }, [borrowsFetched, data.action.actionProgram, data.asin, fetchInProgress]);

    // Fetch metadata
    useEffect(() => {
        if (!hasFetchedMetadata) {
            setHasFetchedMetadata(true);
            getQvMetadata(data.asin).then((loadedMetadata) => {
                setMetadata(loadedMetadata);
                debug.log(`ReturnAndBorrowWithSave: Loaded metadata for ASIN ${data.asin}, isHearted: ${loadedMetadata.isHearted}`);
            }).catch((error) => {
                debug.error("Failed to load metadata for ReturnAndBorrowWithSave", error);
                // Keep the basic metadata with just the ASIN
                setMetadata({ asin: data.asin });
            });
        }
    }, [data.asin, hasFetchedMetadata]);

    useEffect(() => {
        fetchBorrows();
    }, [fetchBorrows]);

    useEffect(() => {
        focusHelper.requestFocus(a11yInitialTarget.current);
    }, []);

    const selectItem = (index: number) => setSelectedItems([...selectedItems, index]);

    const deselectItem = (index: number) => setSelectedItems(selectedItems.filter((it) => it !== index));

    const toggleItem = (index: number) => {
        const isSelected: boolean = selectedItems.indexOf(index) !== -1;
        if (isSelected) {
            deselectItem(index);
        } else {
            selectItem(index);
        }
    };

    const returnAndBorrow = () => {
        setReturnInProgress(true);
        Promise.all(selectedItems.map((index) => acquisitionManagerContext.returnAsin(borrows[index]?.asin || "")))
            .then(() => {
                const asin = data.asin;
                const actionType = data.action.actionType;
                acquisitionManagerContext
                    .redeemOfferByActionType(asin, actionType, callback)
                    .then(onSuccess((result) => {
                        debug.log(`Success ${asin} - ${actionType}: ${result.reasonCode}`);
                    }))
                    .then(onFailure((result) => {
                        debug.error(`Failure ${asin} - ${actionType}: ${result.reasonCode}`);
                    }))
                    .catch(error => {
                        debug.error(`Failed to redeem offer ${asin} - ${actionType} error: ${error}`);
                        return error;
                    });
            })
            .finally(() => {
                dismiss();
            });
    };

    // Helper function for button text
    const getButtonText = () => {
        return isBookInWishlist ? translations.getText("saved") : translations.getText("save");
    };

    const truncateTitle = (title: string, maxLength = 50): string => {
        if (title.length <= maxLength) {
            return title;
        }
        return title.substring(0, maxLength).trim() + "...";
    };
    
    // Helper function to get the description text
    const getDescriptionText = () => {
        if (!hasFetchedMetadata) {
            return <Spinner />;
        }
        if (isBookInWishlist) {
            return translations.formatText("return-with-saved-title", {titleName: truncateTitle(metadata?.title ?? "")});
        }
        return translations.formatText("return-borrow-save-description", {titleName: truncateTitle(metadata?.title ?? "")});
    };

    const heartedSuccess = (response: any) => {
        if (response.response?.wishlists && response.response?.wishlists?.[0]?.name) {
            workflowContext.infoMessage(translations.formatText("heart-list-info-message", {"listName": response.response?.wishlists?.[0].name}));
        } else {
            workflowContext.infoMessage(translations.getText("heart-info-message"));
        }
    };

    const unheartedSuccess = (response: any[]) => {
        if (response.length === 1 && response[0].wishlistName) {
            workflowContext.infoMessage(translations.formatText("unheart-list-info-message", {"listName": response[0].wishlistName}));
        } else {
            workflowContext.infoMessage(translations.getText("unheart-all-info-message"));
        }
    };

    // Handle Save/Remove toggle
    const handleSave = useCallback(() => {
        if (saveInProgress) {
            return;
        }
        setSaveInProgress(true);
        
        if (isBookInWishlist) {
            // Remove from wishlist
            heartCxContext.unheartAsin(data.asin);
            removeFromWishlistsGQL(data.asin, undefined)
                .then(unheartedSuccess)
                .catch((error) => {
                    debug.error("Remove from wishlist failed", error);
                    // Revert the HeartCx state on error
                    heartCxContext.heartAsin(data.asin);
                    workflowContext.infoMessage(translations.getText("add-to-list-failure-text"));
                })
                .finally(() => {
                    setSaveInProgress(false);
                    dismiss();
                });
        } else {
            // Add to wishlist
            heartCxContext.heartAsin(data.asin);
            addToWishlist(data.asin, undefined)
                .then(heartedSuccess)
                .catch((error) => {
                    debug.error("Add to wishlist failed", error);
                    // Revert the HeartCx state on error
                    heartCxContext.unheartAsin(data.asin);
                    workflowContext.infoMessage(translations.getText("add-to-list-failure-text"));
                })
                .finally(() => {
                    setSaveInProgress(false);
                    dismiss();
                });
        }
    }, [saveInProgress, isBookInWishlist, data.asin, dismiss, heartCxContext, translations, workflowContext, unheartedSuccess, heartedSuccess]);

    const logoForProgram = getProgramLogo(data.action.actionProgram.programCode, deviceContext.theme, deviceContext.domain, deviceContext.enableKuRebrand);
    const itemContainerClassName = `${styles.itemContainer} ${styles[deviceContext.theme]}`;

    return (
        <div className={itemContainerClassName}>
            <div className={styles.chevronIconContainer}>
                <div 
                    className={styles.chevronIcon} 
                    onClick={dismiss} 
                    role="button" 
                    tabIndex={0} 
                    aria-label={translations.getText("dismiss")}
                />
            </div>
            {/* Add to library title */}
            <header className={styles.title} ref={a11yInitialTarget} tabIndex={-1}>
                {translations.getText("add-to-library-title")}
            </header>
            
            {/* Divider line */}
            <div className={styles.divider}></div>
            
            { logoForProgram && (
                <div className={styles.logoContainer}>
                    <img className={`${styles.subscriptionProgramImage} ${styles[logoForProgram.extraStyleName || ""]}`} src={logoForProgram.src} alt={translations.getText(logoForProgram.altKey)} />
                </div>
            )}
            
            <header className={styles.header}>
                {translations.getText("borrow-limit-reached")}
            </header>
            <header className={styles.headerDescription}>
                {getDescriptionText()}
            </header>

            <main className={styles.main}>
                {!borrowsFetched && (<div className={styles.spinnerBox}><Spinner /></div>)}

                {borrowsFetched && (
                    <div role="fieldset" aria-multiselectable="true" className={styles.horizontalScroller}>
                        {borrows.map((item, index) => {
                            const isSelected: boolean = selectedItems.indexOf(index) !== -1;
                            return (
                                <BookImage
                                    key={item.loanId}
                                    item={item}
                                    isSelected={isSelected}
                                    selectionToggle={() => toggleItem(index)}
                                />
                            );
                        })}
                    </div>
                )}
            </main>

            <footer className={styles.footer}>
                <div className={styles.buttonContainer}>
                    {/* Return and Continue button section - comes first */}
                    {selectedItems.length > 0 && (
                        <button className={styles.returnButton} onClick={returnAndBorrow} disabled={returnInProgress}>
                            { returnInProgress 
                                ? (<Spinner size="min(1rem, 28px)" />)
                                : translations.getText("return-and-continue")
                            }
                        </button>
                    )}
                    {selectedItems.length === 0 && (
                        <button className={`${styles.returnButton}`} disabled>
                            {translations.getText("return-and-continue")}
                        </button>
                    )}
                    
                    <div className={styles.separator}>
                        {translations.getText("or")}
                    </div>

                    {/* Save button using HeartCx for consistency - comes after separator */}
                    <button 
                        className={`${styles.saveButton} ${(saveInProgress || !hasFetchedMetadata) ? styles.loading : ''}`} 
                        disabled={saveInProgress || !hasFetchedMetadata}
                        onClick={handleSave}
                        aria-label={isBookInWishlist ? translations.getText("hearted-state-a11y-label") : translations.getText("unhearted-state-a11y-label")}
                    >
                        {isBookInWishlist ? <FilledHeartIcon /> : <UnfilledHeartIcon />}
                        {getButtonText()}
                            
                    </button>
                </div>
            </footer>
        </div>
    );
};

export default ReturnAndBorrowWithSave;
